// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { zodResolver } from "@hookform/resolvers/zod";
import { TriangleAlert } from "lucide-react";
import { useEffect, useImperativeHandle, useRef } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { z } from "zod";
import { useEditStoreMutation, useStoreDetailQuery } from "@/api/hooks/use-stores";
import { SaveDiscardPane } from "@/components/shared/save-discard-pane";
import { VirtualSelectPopover } from "@/components/shared/virtual-select-popover";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { useBackofficeContext } from "@/hooks/use-backoffice-context";
import { useAuth } from "@/lib/auth/auth-provider";
import { PAGE_PERMISSIONS } from "@/lib/auth/page-permissions";
import { TIMEZONE_OPTIONS } from "@/lib/timezones";
import { cn } from "@/lib/utils";

const operationsSchema = z.object({
  details: z.object({
    timeZone: z.string().min(1, "Required"),
    openTime: z.string().min(1, "Required"),
    closeTime: z.string().min(1, "Required"),
  }),
  cameras: z.object({
    deviceType: z.literal("esp32"),
    onTime: z.string().min(1, "Required"),
    offTime: z.string().min(1, "Required"),
  }),
  impactSettings: z.object({
    minWorkable: z.number().min(0),
    sortingLogic: z.string(),
    priorityLogic: z.string(),
    isPromoOnTop: z.boolean(),
    isAssociatesCanSort: z.boolean(),
    isShowDsdItems: z.boolean(),
    isShowNonWorkable: z.boolean(),
    // nullish so the null values the API returns (no override set) pass validation
    maxInventoryLevelAge: z.number().nullish(),
    filterUpcSources: z.string().nullish(),
    filterUpcSourcesForHooks: z.string().nullish(),
  }),
  matchSettings: z.object({
    // nullish so the null values the API returns (no labeler setting set) pass validation
    isHidePog: z.boolean().nullish(),
    isDisableIngress: z.boolean().nullish(),
    isExcludeFromQueue: z.boolean().nullish(),
    cooldownHours: z.number().nullish(),
    isDiscrepDisableIngress: z.boolean().nullish(),
  }),
});

type OperationsValues = z.infer<typeof operationsSchema>;

export interface StoreOperationsTabHandle {
  reset: () => void;
}

interface StoreOperationsTabProps {
  storeName: string;
  onDirtyChange?: (dirty: boolean) => void;
  ref?: React.Ref<StoreOperationsTabHandle>;
}

const DIRTY_HIGHLIGHT = "bg-sidebar-accent-hover transition-colors";

const TIME_STEP_MINUTES = 15;
const MAX_TIME_MINUTES = 23 * 60 + 59;

function timeStringToMinutes(value: string): number {
  if (!value) return 0;
  const [h, m] = value.split(":").map(Number);
  return (h ?? 0) * 60 + (m ?? 0);
}

function minutesToTimeString(value: number): string {
  const h = Math.floor(value / 60);
  const m = value % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

function normalizeStoreForForm(store: OperationsValues): OperationsValues {
  return {
    ...store,
    details: {
      ...store.details,
      openTime: store.details.openTime?.slice(0, 5) ?? "",
      closeTime: store.details.closeTime?.slice(0, 5) ?? "",
    },
    cameras: {
      ...store.cameras,
      onTime: store.cameras.onTime?.slice(0, 5) ?? "",
      offTime: store.cameras.offTime?.slice(0, 5) ?? "",
    },
  };
}

export function StoreOperationsTab({ storeName, onDirtyChange, ref }: StoreOperationsTabProps) {
  const { t } = useTranslation("back-office");
  const { hasPermission } = useAuth();
  const canEdit = hasPermission(PAGE_PERMISSIONS.storesOperationsEdit);
  const { data: storeRaw } = useStoreDetailQuery(storeName);
  const { data: context } = useBackofficeContext();
  const editStore = useEditStoreMutation(storeName);

  const store = storeRaw as unknown as (OperationsValues & Record<string, unknown>) | undefined;
  // The impact settings record can be absent on a freshly created / empty store
  const isMissingData = !!storeRaw && !storeRaw.impactSettings;

  const emptyDefaults: OperationsValues = {
    details: { timeZone: "", openTime: "", closeTime: "" },
    cameras: { deviceType: "esp32", onTime: "", offTime: "" },
    impactSettings: {
      minWorkable: 0,
      sortingLogic: "",
      priorityLogic: "",
      isPromoOnTop: false,
      isAssociatesCanSort: false,
      isShowDsdItems: false,
      isShowNonWorkable: false,
    },
    matchSettings: {},
  };

  const form = useForm<OperationsValues>({
    resolver: zodResolver(operationsSchema),
    defaultValues: store ? normalizeStoreForForm(store) : emptyDefaults,
  });

  useEffect(
    function resetFormWhenStoreDataLoads() {
      if (store) {
        form.reset(normalizeStoreForForm(store));
      }
    },
    [store, form],
  );

  async function onSubmit(values: OperationsValues) {
    try {
      await editStore.mutateAsync({
        operations: {
          details: values.details,
          cameras: values.cameras,
          // The API returns nullable fields as null; convert to undefined so they're omitted (not sent as null)
          impactSettings: {
            ...values.impactSettings,
            maxInventoryLevelAge: values.impactSettings.maxInventoryLevelAge ?? undefined,
            filterUpcSources: values.impactSettings.filterUpcSources ?? undefined,
            filterUpcSourcesForHooks: values.impactSettings.filterUpcSourcesForHooks ?? undefined,
          },
          matchSettings: {
            isHidePog: values.matchSettings.isHidePog ?? undefined,
            isDisableIngress: values.matchSettings.isDisableIngress ?? undefined,
            isExcludeFromQueue: values.matchSettings.isExcludeFromQueue ?? undefined,
            cooldownHours: values.matchSettings.cooldownHours ?? undefined,
            isDiscrepDisableIngress: values.matchSettings.isDiscrepDisableIngress ?? undefined,
          },
        },
      });
      toast.success(
        t("stores.operations.notifications.updated", {
          name: (store as any)?.legibleName ?? storeName,
        }),
      );
      // Reset to the form's own current values so dirty state clears on 200 (independent of refetch)
      form.reset(form.getValues());
    } catch {
      // Error toast handled by API middleware
    }
  }

  const { isDirty, dirtyFields } = form.formState;

  useImperativeHandle(ref, () => ({ reset: () => form.reset() }));

  const onDirtyChangeRef = useRef(onDirtyChange);
  onDirtyChangeRef.current = onDirtyChange;
  useEffect(
    function notifyParentOfDirtyState() {
      onDirtyChangeRef.current?.(isDirty);
    },
    [isDirty],
  );

  return (
    <div className="max-w-2xl space-y-6 p-6">
      {isMissingData && (
        <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-200">
          <TriangleAlert className="h-4 w-4 shrink-0 mt-0.5" />
          <span>{t("stores.missingDataWarning")}</span>
        </div>
      )}
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="details.timeZone"
              render={({ field }) => (
                <FormItem className="col-span-2">
                  <FormLabel>{t("stores.info.fields.timeZone")}</FormLabel>
                  <FormControl>
                    <VirtualSelectPopover
                      value={field.value}
                      onChange={field.onChange}
                      options={TIMEZONE_OPTIONS}
                      placeholder={t("stores.form.selectTimezone")}
                      searchPlaceholder={t("stores.form.searchTimezones")}
                      emptyText={t("stores.form.noTimezones")}
                      className={cn(dirtyFields.details?.timeZone && DIRTY_HIGHLIGHT)}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormItem
              className={cn(
                "col-span-2 rounded-md p-2 pb-4 -m-2 -mb-2",
                (dirtyFields.details?.openTime || dirtyFields.details?.closeTime) && DIRTY_HIGHLIGHT,
              )}
            >
              <div className="flex items-baseline justify-between">
                <FormLabel>{t("stores.operations.fields.storeHours")}</FormLabel>
                <span className="font-mono text-sm tabular-nums text-muted-foreground">
                  {form.watch("details.openTime")?.slice(0, 5) || "—"} –{" "}
                  {form.watch("details.closeTime")?.slice(0, 5) || "—"}
                </span>
              </div>
              <FormControl>
                <Slider
                  min={0}
                  max={MAX_TIME_MINUTES}
                  step={TIME_STEP_MINUTES}
                  value={[
                    timeStringToMinutes(form.watch("details.openTime")),
                    timeStringToMinutes(form.watch("details.closeTime")),
                  ]}
                  onValueChange={(values) => {
                    form.setValue("details.openTime", minutesToTimeString(values[0] ?? 0), {
                      shouldDirty: true,
                      shouldTouch: true,
                    });
                    form.setValue("details.closeTime", minutesToTimeString(values[1] ?? MAX_TIME_MINUTES), {
                      shouldDirty: true,
                      shouldTouch: true,
                    });
                  }}
                  minStepsBetweenThumbs={1}
                />
              </FormControl>
            </FormItem>
            <FormItem
              className={cn(
                "col-span-2 rounded-md p-2 pb-4 -mx-2 -mb-2",
                (dirtyFields.cameras?.onTime || dirtyFields.cameras?.offTime) && DIRTY_HIGHLIGHT,
              )}
            >
              <div className="flex items-baseline justify-between">
                <FormLabel>{t("stores.operations.fields.cameraHours")}</FormLabel>
                <span className="font-mono text-sm tabular-nums text-muted-foreground">
                  {form.watch("cameras.onTime")?.slice(0, 5) || "—"} –{" "}
                  {form.watch("cameras.offTime")?.slice(0, 5) || "—"}
                </span>
              </div>
              <FormControl>
                <Slider
                  min={0}
                  max={MAX_TIME_MINUTES}
                  step={TIME_STEP_MINUTES}
                  value={[
                    timeStringToMinutes(form.watch("cameras.onTime")),
                    timeStringToMinutes(form.watch("cameras.offTime")),
                  ]}
                  onValueChange={(values) => {
                    form.setValue("cameras.onTime", minutesToTimeString(values[0] ?? 0), {
                      shouldDirty: true,
                      shouldTouch: true,
                    });
                    form.setValue("cameras.offTime", minutesToTimeString(values[1] ?? MAX_TIME_MINUTES), {
                      shouldDirty: true,
                      shouldTouch: true,
                    });
                  }}
                  minStepsBetweenThumbs={1}
                />
              </FormControl>
            </FormItem>
          </div>

          <Separator />

          <h3 className="text-base font-semibold">{t("stores.info.impactSettings.title")}</h3>

          <div className="grid grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="impactSettings.priorityLogic"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("stores.operations.fields.priorityLogic")}</FormLabel>
                  <FormControl>
                    <VirtualSelectPopover
                      value={field.value}
                      onChange={field.onChange}
                      options={(context?.impactPriorityLogics ?? []).map((option) => ({
                        label: option,
                        value: option,
                      }))}
                      placeholder={t("stores.operations.selectPriorityLogic")}
                      searchPlaceholder={t("stores.operations.searchOption")}
                      emptyText={t("stores.operations.noOption")}
                      className={cn(dirtyFields.impactSettings?.priorityLogic && DIRTY_HIGHLIGHT)}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="impactSettings.sortingLogic"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("stores.operations.fields.sortingLogic")}</FormLabel>
                  <FormControl>
                    <VirtualSelectPopover
                      value={field.value}
                      onChange={field.onChange}
                      options={(context?.impactSortingLogics ?? []).map((option) => ({
                        label: option,
                        value: option,
                      }))}
                      placeholder={t("stores.operations.selectSortingLogic")}
                      searchPlaceholder={t("stores.operations.searchOption")}
                      emptyText={t("stores.operations.noOption")}
                      className={cn(dirtyFields.impactSettings?.sortingLogic && DIRTY_HIGHLIGHT)}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <FormField
            control={form.control}
            name="impactSettings.minWorkable"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("stores.operations.fields.minWorkable")}</FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    {...field}
                    onChange={(e) => field.onChange(Number(e.target.value))}
                    className={cn(dirtyFields.impactSettings?.minWorkable && DIRTY_HIGHLIGHT)}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <div className="grid grid-cols-2 gap-4">
            {(
              [
                ["impactSettings.isPromoOnTop", "stores.operations.fields.isPromoOnTop"],
                ["impactSettings.isAssociatesCanSort", "stores.operations.fields.isAssociatesCanSort"],
                ["impactSettings.isShowDsdItems", "stores.operations.fields.isShowDsdItems"],
                ["impactSettings.isShowNonWorkable", "stores.operations.fields.isShowNonWorkable"],
              ] as const
            ).map(([name, labelKey]) => {
              const subkey = name.split(".")[1] as keyof typeof dirtyFields.impactSettings;
              return (
                <FormField
                  key={name}
                  control={form.control}
                  name={name}
                  render={({ field }) => (
                    <FormItem
                      className={cn(
                        "flex items-center justify-between rounded-lg border p-3",
                        dirtyFields.impactSettings?.[subkey] && DIRTY_HIGHLIGHT,
                      )}
                    >
                      <FormLabel className="font-normal">{t(labelKey)}</FormLabel>
                      <FormControl>
                        <Switch checked={field.value} onCheckedChange={field.onChange} />
                      </FormControl>
                    </FormItem>
                  )}
                />
              );
            })}
          </div>

          <Separator />

          <h3 className="text-base font-semibold">{t("stores.info.matchSettings.title")}</h3>

          <div className="grid grid-cols-2 gap-4">
            {(
              [
                ["matchSettings.isHidePog", "stores.operations.fields.isHidePog"],
                ["matchSettings.isDisableIngress", "stores.operations.fields.isDisableIngress"],
                ["matchSettings.isExcludeFromQueue", "stores.operations.fields.isExcludeFromQueue"],
                ["matchSettings.isDiscrepDisableIngress", "stores.operations.fields.isDiscrepDisableIngress"],
              ] as const
            ).map(([name, labelKey]) => {
              const subkey = name.split(".")[1] as keyof typeof dirtyFields.matchSettings;
              return (
                <FormField
                  key={name}
                  control={form.control}
                  name={name}
                  render={({ field }) => (
                    <FormItem
                      className={cn(
                        "flex items-center justify-between rounded-lg border p-3",
                        dirtyFields.matchSettings?.[subkey] && DIRTY_HIGHLIGHT,
                      )}
                    >
                      <FormLabel className="font-normal">{t(labelKey)}</FormLabel>
                      <FormControl>
                        <Switch checked={field.value ?? false} onCheckedChange={field.onChange} />
                      </FormControl>
                    </FormItem>
                  )}
                />
              );
            })}
          </div>
          <FormField
            control={form.control}
            name="matchSettings.cooldownHours"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("stores.operations.fields.cooldownHours")}</FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    {...field}
                    value={field.value ?? ""}
                    onChange={(e) => field.onChange(e.target.value ? Number(e.target.value) : undefined)}
                    className={cn(dirtyFields.matchSettings?.cooldownHours && DIRTY_HIGHLIGHT)}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {isDirty && (
            <SaveDiscardPane onDiscard={() => form.reset()} saving={editStore.isPending} saveDisabled={!canEdit} />
          )}
        </form>
      </Form>
    </div>
  );
}
